import { TableOutlined } from '@ant-design/icons'
import {
  buildUuid,
  CreatePanelRequest,
  DRILLDOWN_PANEL_INITIAL_DATA,
  DrilldownPanelInitialDataMap,
} from 'features/drilldown'
import {
  AbstractDrilldownPanel,
  DrilldownPanelProps,
} from 'features/drilldown/components/DrilldownViewPanel'
import { HistogramConfigurationPanel } from 'features/drilldown/components/histogram/configPanel/HistogramConfigurationPanel'
import {
  HistogramConfigurationContextProvider,
  HistogramConfigurationProviderProps,
} from 'features/drilldown/components/histogram/contexts/HistogramConfigurationContext'
import { DrilldownHistogram } from 'features/drilldown/components/histogram/DrilldownHistogram'
import {
  DrilldownTableConfigContextProvider,
  DrilldownTableConfigContextProviderProps,
} from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableConfigPanel } from 'features/drilldown/components/table/config/DrilldownTableConfigPanel'
import { DrilldownTable } from 'features/drilldown/components/table/DrilldownTable'
import _ from 'lodash'
import React, { FC, ReactNode } from 'react'
import { DrilldownPanelType } from 'types'

import { DrilldownPanelConfig } from './PanelConfigs'

export const buildPanel = ({
  panelType,
  initialDataOverride,
}: CreatePanelRequest) => {
  const initialData = _.merge(
    _.cloneDeep(DRILLDOWN_PANEL_INITIAL_DATA[panelType]),
    initialDataOverride // TODO validate that the initial data override works
  )
  return {
    id: buildUuid(),
    panelType,
    panelData: initialData,
    filters: new Map(),
    focusItems: undefined,
  }
}

export const PanelRender = ({
  children,
  panelType,
  ...rest
}: DrilldownPanelProps & {
  panelType: DrilldownPanelType
  children: ReactNode
}) => (
  <AbstractDrilldownPanel {...rest} panelType={panelType}>
    {children}
  </AbstractDrilldownPanel>
)

export const createPanelConfig = <T extends keyof DrilldownPanelInitialDataMap>(
  panelType: T,
  initialData: DrilldownPanelInitialDataMap[T],
  Component: React.FC,
  options: {
    isGloballyAvailable: boolean
    icon?: ReactNode
    configPanel?: ReactNode
    panelPadding?: number
    componentProps?: Record<string, unknown>
  }
): DrilldownPanelConfig => ({
  name: panelType,
  icon: options?.icon ?? <TableOutlined />,
  tooltip: initialData.title,
  isGloballyAvailable: options.isGloballyAvailable,
  render: (props) => (
    <PanelRender
      {...props}
      panelType={panelType}
      configPanel={options.configPanel}
      panelPadding={options.panelPadding}>
      <Component {...options.componentProps} />
    </PanelRender>
  ),
})

export const HistogramPanelRender: FC<{
  panelProps: DrilldownPanelProps
  histoConfigProps: HistogramConfigurationProviderProps
}> = ({ panelProps, histoConfigProps }) => {
  return (
    <HistogramConfigurationContextProvider {...histoConfigProps}>
      <PanelRender
        {...panelProps}
        panelType={panelProps.panelType}
        configPanel={<HistogramConfigurationPanel />}>
        <DrilldownHistogram />
      </PanelRender>
    </HistogramConfigurationContextProvider>
  )
}

export const TablePanelRender: FC<{
  panelProps: DrilldownPanelProps
  configProps: Omit<DrilldownTableConfigContextProviderProps, 'children'>
}> = ({ panelProps, configProps }) => {
  return (
    <DrilldownTableConfigContextProvider {...configProps}>
      <PanelRender
        {...panelProps}
        panelType={panelProps.panelType}
        configPanel={<DrilldownTableConfigPanel />}>
        <DrilldownTable />
      </PanelRender>
    </DrilldownTableConfigContextProvider>
  )
}
